import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import NueraLogo from "@/components/NueraLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await register(form);
    setLoading(false);
    if (res.ok) { toast.success("Account created — let's set up your brand"); navigate("/onboarding"); }
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8"><NueraLogo className="h-8 w-auto" /></div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Create your account</h1>
          <p className="mt-2 text-muted-foreground">Start seeing your marketing come to life.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="name">Your name</Label>
              <Input id="name" data-testid="register-name" value={form.name} onChange={set("name")} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="company">Company / brand name</Label>
              <Input id="company" data-testid="register-company" value={form.company_name} onChange={set("company_name")} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="register-email" type="email" value={form.email} onChange={set("email")} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="register-password" type="password" value={form.password} onChange={set("password")} required minLength={6} />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="register-error">{error}</p>}
            <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full rounded-full bg-brand-primary text-brand-primary-foreground hover:opacity-90">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create account
            </Button>
          </form>
          <p className="mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link to="/login" data-testid="to-login" className="font-semibold text-brand-primary hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
      <div className="relative hidden overflow-hidden bg-primary lg:block">
        <img src="https://images.unsplash.com/photo-1541339907198-e08756dedf3f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400" alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-primary via-primary/70 to-transparent" />
        <div className="absolute bottom-0 p-12 text-primary-foreground">
          <p className="font-heading text-3xl font-bold leading-tight">One command centre<br />for every channel.</p>
          <p className="mt-3 max-w-md text-primary-foreground/70">Connect your channels once. Nuera keeps your data fresh, historical and beautifully visual.</p>
        </div>
      </div>
    </div>
  );
}
